/**
 * Shared draft-card UI primitives.
 *
 * Imported by:
 *   • DraftPage.jsx — live draft pool grid + pack draft.
 *   • DraftedDeckModal — post-draft deck-review detail view.
 *
 * Owns the "printed card" look-and-feel (cream parchment background,
 * black bold typography, rarity / 1-of-1 marker, diamonds, card number)
 * so both pages render the same card and rarity styling stays in sync.
 */

import React from "react";
import { RARITY_BADGE_STYLE, rarityBadgeStyle } from "@/constants/rarities";

/* ─── Rarity palette ─────────────────────────────────────────────────────── */

// Re-exported under legacy names so existing callers need no changes.
export const DRAFT_RARITY_STYLE = RARITY_BADGE_STYLE;
export const draftRarityStyle = rarityBadgeStyle;

// Subtle per-rarity border accent for the cream card front.
export const FRONT_RARITY_ACCENT = {
  common:    "border-stone-400/70   shadow-stone-500/20",
  uncommon:  "border-emerald-500/60 shadow-emerald-500/20",
  rare:      "border-blue-500/60    shadow-blue-500/20",
  iconic:    "border-amber-500/70   shadow-amber-500/30",
  legendary: "border-purple-500/60  shadow-purple-500/20",
  exclusive: "border-orange-500/60  shadow-orange-500/20",
  junk_wax:  "border-stone-500/40   shadow-stone-500/10",
};

/* ─── Per-card display helpers ───────────────────────────────────────────── */

// Stable 2-digit "printed" number from the card id. Display-only flavour;
// same id always renders the same number across sessions / clients.
export function cardNumberFromId(id) {
  if (!id) return "—";
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return String(Math.abs(h) % 99 + 1).padStart(2, "0");
}

// Diamond count under the card — one per rarity tier so every card always
// shows diamonds and the visual hierarchy matches rarity, not raw stat values.
const RARITY_DIAMONDS = {
  junk_wax: 1, common: 2, uncommon: 2, rare: 3, iconic: 4, legendary: 4, exclusive: 4,
};
export function cardDiamondCount(card) {
  if (!card) return 0;
  return RARITY_DIAMONDS[card.rarity] ?? 1;
}

/* ─── DraftCardChip — full printed card front ────────────────────────────── */

/**
 * Cream-parchment "printed card" front used everywhere a draft card is
 * presented at full size (pack draft, pool grids, deck-review modal).
 *
 * Props:
 *   card     — manager-card record ({id, name, description, rarity, ...}).
 *   onPick   — optional click handler. Called with `card.id` when not disabled.
 *   disabled — when true, no hover lift, no click.
 *   badge    — optional `{label, classes}` overlay (Yours / Locked / Picked / Burned).
 *   dim      — when true, opacity + desaturate (opponent-locked, burned, etc.).
 */
export function DraftCardChip({ card, onPick, disabled, badge, dim }) {
  const rarityLabel = (DRAFT_RARITY_STYLE[card?.rarity]?.label) || "—";
  const accent = FRONT_RARITY_ACCENT[card?.rarity] || FRONT_RARITY_ACCENT.common;
  const diamonds = cardDiamondCount(card);
  const num = cardNumberFromId(card?.id);
  return (
    <button
      type="button"
      onClick={() => !disabled && onPick && onPick(card?.id)}
      disabled={disabled}
      title={card?.description || card?.name || card?.id}
      // `aspect-[5/7]` is the *intrinsic* ratio used when the parent has no
      // determined height (e.g. DraftedDeckModal's max-w wrapper). `h-full`
      // + `w-full` let the button stretch to fill height-constrained parents
      // — the pack / pool grids use `auto-rows-fr` plus an `h-full` chain
      // through the PackCard wrappers so every chip in a row resolves to the
      // same height regardless of description length.
      className={`relative aspect-[5/7] h-full w-full min-h-[180px] rounded-lg border-2 ${accent}
        bg-[#f5ead0] text-black flex flex-col p-3 overflow-hidden shadow-md
        transition-all
        ${dim ? "opacity-40 saturate-50" : ""}
        ${disabled
          ? "cursor-not-allowed"
          : "hover:shadow-xl hover:-translate-y-0.5 cursor-pointer"}`}
    >
      {/* Top row: rarity · 1/1 marker */}
      <div className="flex items-baseline justify-between font-black text-[10px] tracking-wider">
        <span>{rarityLabel}</span>
        <span className="font-mono">1/1</span>
      </div>

      {/* Name + divider + description — vertically centered in the card body.
          Description is intentionally NOT line-clamped: rules text is the
          point of the card during a draft, so we'd rather let a long card
          push past `aspect-[5/7]` (grids use `auto-rows-fr`, so a tall card
          just stretches its whole row uniformly) than ellipsis-truncate it. */}
      <div className="flex-1 flex flex-col items-center justify-center px-2">
        <h3 className="font-black text-center leading-tight"
            style={{ fontSize: "clamp(0.85rem, 1.3vw, 1.1rem)" }}>
          {card?.name || card?.id}
        </h3>
        <div className="w-full h-px bg-black mt-2" />
        <p className="text-center font-bold leading-snug mt-2 w-full"
           style={{ fontSize: "clamp(0.55rem, 0.85vw, 0.7rem)" }}>
          {card?.description || ""}
        </p>
      </div>

      {/* Bottom row: diamonds · stable card number */}
      <div className="flex items-baseline justify-between text-xs font-black pt-1">
        <span className="tracking-[-0.05em] leading-none">
          {diamonds > 0 ? "◆".repeat(diamonds) : ""}
        </span>
        <span className="font-mono">{num}</span>
      </div>

      {/* Status badge overlay — Yours / Locked / Picked / Burned. */}
      {badge && (
        <span className={`absolute -top-1.5 -right-1.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border shadow ${badge.classes}`}>
          {badge.label}
        </span>
      )}
    </button>
  );
}
